import { SpeechMark } from "../typings";

import { expandRangeAroundNode } from "./expandRangeAroundNode";
import { extractFirstWordInRange } from "./extractFirstWordInRange";
import { getActiveWordIndex } from "./getActiveWordIndex";
import { isValidWord } from "./isValidWord";

type Args = {
  range: Range;
  progressInMs: number;
  speechMarks: SpeechMark[];
  prevIndex?: number;
};

/**
 * Returns a new word node in case the karaoke has moved to a new word.
 *
 * NB: This method manipulates the passed range in order to optimize subsequent
 * calls. Therefore, in case audio is seeked or similar during karaoke, the range
 * must be reset to the original range.
 */
export function getNewWordNode({
  progressInMs,
  speechMarks,
  prevIndex = -1,
  range,
}: Args):
  | {
      newWordIndex: number;
      highlightNode: HTMLSpanElement;
    }
  | undefined {
  let currentWordIndex = getActiveWordIndex(speechMarks, progressInMs);
  let currentWordRange: Range | undefined;

  // In case we've moved to a new word since last checked, then attempt to extract
  // the new range...
  if (currentWordIndex !== undefined && currentWordIndex > prevIndex) {
    // ... by first moving our range to hopefully be right before the desired word
    if (currentWordIndex > 0) {
      for (let i = prevIndex; i < currentWordIndex; i++) {
        moveRangeToNextWord(range);
      }
    }

    currentWordRange = extractFirstWordInRange(range);
    let currentWord = currentWordRange?.toString() ?? "";

    // ... however, if the obfuscating HTML was found in the range, then skip it
    // until the currentWordRange contains the desired word
    while (!isValidWord(currentWord)) {
      // In case we cannot move the range anymore, then break out!
      if (!moveRangeToNextWord(range)) {
        break;
      }

      currentWordIndex++;
      currentWordRange = extractFirstWordInRange(range);
      currentWord = currentWordRange?.toString() ?? "";
    }

    // In case we successfully found a new word range, then insert our highlight
    // wrapper around it and return
    if (currentWordRange) {
      const highlightNode = document.createElement("span");
      highlightNode.dataset.gPolly = "true";

      // Ensure we inclde all relevant HTMLElements around our word
      expandRangeAroundNode(currentWordRange);

      // ... and then remove it from the DOM to insert it into our highlightnode
      highlightNode.appendChild(currentWordRange.extractContents());
      currentWordRange.insertNode(highlightNode);

      return {
        highlightNode,
        newWordIndex: currentWordIndex,
      };
    }
  }
}

/**
 * Moves a range to the next valid word in the range. In case this operation
 * couldn't be successfully completed, then false is returned.
 */
function moveRangeToNextWord(range: Range): boolean {
  const nextRange = extractFirstWordInRange(range);

  // Push the remaining range forwards, slicying away the first
  // word within it
  if (nextRange !== undefined) {
    range.setStart(nextRange.endContainer, nextRange.endOffset);

    return true;
  }

  return false;
}
